<template>
    <div class="pcc">
        <div class="pcc-grid" v-if="groups.visible.length">
            <ProductChannelLinkCardCmp
                v-for="storeChannel in groups.visible"
                :key="cardKey(storeChannel)"
                v-bind="cardProps(storeChannel)"
                @associate="associate(storeChannel, $event)"
                @checked="$emit('checked', storeChannel, $event)" />
        </div>

        <template v-if="groups.hidden.length">
            <InactiveChannelsToggleCmp
                class="pcc-toggle"
                :channels="groups.hidden.map((sc) => sc.channel)"
                :open="showInactive"
                @toggle="showInactive = !showInactive" />

            <div class="pcc-grid is-inactive" v-if="showInactive">
                <ProductChannelLinkCardCmp
                    v-for="storeChannel in groups.hidden"
                    :key="cardKey(storeChannel)"
                    v-bind="cardProps(storeChannel)"
                    @associate="associate(storeChannel, $event)"
                    @checked="$emit('checked', storeChannel, $event)" />
            </div>
        </template>

        <!-- Na grade quem associa é a tela da grade (ela conhece as variações) -->
        <ProductChannelAssociationModalCmp v-if="canAssociate && !gradeMode" ref="associationModal" />
    </div>
</template>

<script>
import ProductChannelLinkCardCmp from './ProductChannelLinkCardCmp.vue'
import ProductChannelAssociationModalCmp from './ProductChannelAssociationModalCmp.vue'
import InactiveChannelsToggleCmp from '@/channel/components/InactiveChannelsToggleCmp.vue'
import { splitStoreChannelsByChannelState } from '@/channel/utils/ChannelListSorter'
import { getChannelSearchProfile } from '@/channel/utils/ChannelProductAssociationHelper'

/**
 * Cards dos canais de um produto na ordem da listagem de canais: ativos
 * primeiro, por canal e nome. Canais desativados nas configurações ficam
 * recolhidos, como na rota /canais.
 */
export default {
    name: 'ProductChannelCardListCmp',
    components: {
        ProductChannelLinkCardCmp,
        ProductChannelAssociationModalCmp,
        InactiveChannelsToggleCmp,
    },
    props: {
        storeChannels: {
            type: Array,
            required: true,
        },
        channelList: {
            type: Array,
            default: () => [],
        },
        idStore: {
            type: [Number, String],
            default: null,
        },
        idMuven: {
            type: [Number, String],
            default: null,
        },
        checkInChannel: {
            type: Boolean,
            default: true,
        },
        idLabel: {
            type: String,
            default: 'ID no canal',
        },
        showStock: {
            type: Boolean,
            default: true,
        },
        // Nome, SKU, EAN e imagem do produto, mostrados na associação guiada
        productSummary: {
            type: Object,
            default: null,
        },
        // Grade: os cards conferem o id do pai e o evento `associate` sobe para a tela da grade
        gradeMode: {
            type: Boolean,
            default: false,
        },
        // Associação só pela tela de associação
        manageOnly: {
            type: Boolean,
            default: false,
        },
        // Grade: (storeChannel) => variações do Muven com o id de hoje neste canal, para a régua do card
        gridVariationsOf: {
            type: Function,
            default: null,
        },
    },
    data() {
        return {
            showInactive: false,
        }
    },
    computed: {
        // Associar pelo card só faz sentido quando o canal é consultado e o produto é conhecido
        canAssociate() {
            return this.checkInChannel && !this.manageOnly && !!this.idStore && !!this.idMuven && (this.gradeMode || !!this.productSummary)
        },
        groups() {
            const {active, inactive} = splitStoreChannelsByChannelState(this.storeChannels, this.channelList)

            // Só recolhe quando sobra algum ativo à vista; senão a seção ficaria só com o resumo
            if (!active.length || !inactive.length) {
                return {visible: [...active, ...inactive], hidden: [], inactive}
            }

            return {visible: active, hidden: inactive, inactive}
        },
    },
    methods: {
        cardKey(storeChannel) {
            return `${this.idStore}-${storeChannel.channel.id}-${storeChannel.idExternalChannel}`
        },
        cardProps(storeChannel) {
            return {
                storeChannel,
                idStore: storeChannel.idStore || this.idStore,
                idMuven: this.idMuven,
                checkInChannel: this.checkInChannel,
                idLabel: this.idLabel,
                showStock: this.showStock,
                channelIsActive: !this.groups.inactive.includes(storeChannel),
                gradeMode: this.gradeMode,
                gridVariations: this.gradeMode && this.gridVariationsOf ? this.gridVariationsOf(storeChannel) : [],
                // Canal multi também troca pelo card; só a grade continua indo para a tela de associação
                associable: this.canAssociate && (!this.isMultiAssociation(storeChannel) || !this.gradeMode)
                    && getChannelSearchProfile(storeChannel.channel.enumChannel).supportsSearch,
                associationUrl: this.associationUrlOf(storeChannel),
            }
        },
        associationUrlOf(storeChannel) {
            const id = this.channelIdOf(storeChannel)

            if (this.isMultiAssociation(storeChannel) && (this.gradeMode || !this.canAssociate)) {
                return `/canais/${id}/configuracoes/associacoes/multi/produtos`
            }

            return this.manageOnly ? `/canais/${id}/configuracoes/associacoes/produtos` : null
        },
        channelIdOf(storeChannel) {
            return storeChannel.idChannel || storeChannel.channel.id
        },
        // Mercado Livre com várias contas de anúncio por produto usa outra tela e outros endpoints
        // Mercado Livre multi: cada card é uma linha própria; o modal pode trocar, adicionar outro ou remover
        async associateMulti(storeChannel, current) {
            const id = String(this.channelIdOf(storeChannel))
            const siblingIds = this.storeChannels
                .filter((sc) => sc !== storeChannel && String(this.channelIdOf(sc)) === id && !!sc.idExternalChannel)
                .map((sc) => String(sc.idExternalChannel))

            const result = await this.$refs.associationModal.open({
                storeChannel,
                idStore: this.idStore,
                idMuven: this.idMuven,
                product: this.productSummary,
                current,
                multi: true,
                siblingIds,
            })

            if (result) {
                this.$emit('listing-changed', storeChannel, result)
            }
        },
        isMultiAssociation(storeChannel) {
            const configured = this.channelList.find((c) => String(c.id) === String(this.channelIdOf(storeChannel)))

            return !!configured && !!configured.allowsMultiAssociation && configured.allowsMultiAssociation()
        },
        async associate(storeChannel, current) {
            if (this.gradeMode) {
                this.$emit('associate', storeChannel, current)
                return
            }

            if (this.isMultiAssociation(storeChannel)) {
                await this.associateMulti(storeChannel, current)
                return
            }

            const newId = await this.$refs.associationModal.open({
                storeChannel,
                idStore: this.idStore,
                idMuven: this.idMuven,
                product: this.productSummary,
                current,
            })

            if (newId !== undefined) {
                this.$emit('updated', storeChannel, newId)
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.pcc {
    display: grid;
    gap: 14px;
}

.pcc-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 14px;

    &.is-inactive {
        animation: pcc-reveal .3s cubic-bezier(.22, 1, .36, 1) both;
    }
}

@keyframes pcc-reveal {
    from {
        opacity: 0;
        transform: translateY(-4px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .pcc-grid.is-inactive {
        animation: none;
    }
}
</style>
